import { useState } from "react";
import { checkPricing, detectScope } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, AlertTriangle, TrendingUp, Crown, ChevronRight, Sparkles, GitCompare, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const fieldBase = "mt-2 bg-white rounded-2xl h-12 border border-black/10 focus-visible:ring-brand-purple";
const taBase = "mt-2 bg-white rounded-2xl border border-black/10 resize-none focus-visible:ring-brand-purple";
const lbl = "text-sm font-semibold text-foreground/85";

const TIERS = [
  { key: "freelancer", name: "Freelancer", price: "€250 — €1.2k", focus: "Identidad mínima viable", responsibility: "Ejecución limpia", typical: ["Logo", "Paleta", "Tipografías", "Mini guía"], sells: "Estética inicial", avoid: "Estrategia de marca completa", color: "block-purpleSoft" },
  { key: "small", name: "Pequeña empresa", price: "€1.5k — €5k", focus: "Posicionamiento + comunicación", responsibility: "Decisiones estratégicas básicas", typical: ["Mini estrategia", "Identidad", "Kit redes sociales", "Voz de marca"], sells: "Claridad de marca", avoid: "Sistemas globales", color: "block-pink" },
  { key: "startup", name: "Startup", price: "€6k — €20k", focus: "Sistema escalable + lanzamiento", responsibility: "Dirección creativa estratégica", typical: ["Sistema visual escalable", "Identidad verbal", "Dirección creativa", "Assets de lanzamiento", "Sistema preparado para crecer"], sells: "Preparación para escalar", avoid: "Sobreinflar producción", color: "block-lime" },
  { key: "multinational", name: "Multinacional", price: "€35k+", focus: "Implementación global", responsibility: "Liderazgo estratégico + coordinación", typical: ["Alineación entre departamentos", "Múltiples stakeholders", "Documentación avanzada", "Implementación compleja", "Consistencia global", "Reporting", "Soporte continuo", "Localización"], sells: "Reducción de incertidumbre operativa", avoid: "Vender por entregables", color: "block-purple" },
];

export default function ValueLadder() {
  const [active, setActive] = useState("startup");
  const current = TIERS.find((t) => t.key === active);

  const [scopeForm, setScopeForm] = useState({ deliverables: "", strategic_outcomes: "", service_type: "branding", company_size: "startup" });
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeResult, setScopeResult] = useState(null);

  const [priceForm, setPriceForm] = useState({ current_price: 3000, currency: "EUR", service_type: "branding", company_size: "startup", deliverables: "", strategic_responsibility: "" });
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceResult, setPriceResult] = useState(null);

  const runScope = async () => {
    setScopeLoading(true);
    try {
      const r = await detectScope({
        ...scopeForm,
        deliverables: scopeForm.deliverables.split("\n").map((x) => x.trim()).filter(Boolean),
        strategic_outcomes: scopeForm.strategic_outcomes.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      setScopeResult(r);
    } catch (e) {
      toast.error(e.userMessage || "Error detectando inflación");
    } finally { setScopeLoading(false); }
  };

  const runPrice = async () => {
    setPriceLoading(true);
    try {
      const r = await checkPricing({ ...priceForm, current_price: Number(priceForm.current_price), deliverables: priceForm.deliverables.split("\n").map((x) => x.trim()).filter(Boolean) });
      setPriceResult(r);
    } catch (e) {
      toast.error(e.userMessage || "Error chequeando pricing");
    } finally { setPriceLoading(false); }
  };

  return (
    <div className="space-y-10">
      <header className="relative">
        <div className="blob blob-mint w-[220px] h-[220px] -top-6 left-10" />
        <div className="blob blob-coral w-[200px] h-[200px] top-0 right-1/4" style={{ animationDelay: "2s" }} />
        <div className="relative">
          <span className="tag-pill tag-pill-mint mb-5"><GitCompare className="w-3 h-3" /> Escalera de valor</span>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-[-0.04em] leading-[0.95] max-w-3xl">
            Escala tu oferta,<br />
            no tu <span className="scribble-underline scribble-coral">burnout</span>.
          </h1>
          <p className="text-base text-foreground/65 mt-5 max-w-2xl leading-relaxed">
            Selecciona un nivel para ver qué cambia realmente: no la cantidad, sino la responsabilidad, el impacto y la complejidad.
          </p>
        </div>
      </header>

      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-5">
        <div className="space-y-2">
          {TIERS.map((t, i) => (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-testid={`ladder-tier-${t.key}`}
              className={`w-full text-left rounded-2xl p-5 transition-all ${
                active === t.key ? "bg-foreground text-white" : "bg-white border border-black/[0.08] hover:border-foreground"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold ${active === t.key ? "text-white/55" : "text-foreground/45"}`}>0{i + 1}</span>
                  <h3 className="font-display text-lg font-black tracking-[-0.02em]">{t.name}</h3>
                  {t.key === "multinational" && <Crown className="w-3.5 h-3.5" strokeWidth={2} />}
                </div>
                <ChevronRight className="w-4 h-4 opacity-40" strokeWidth={2} />
              </div>
              <div className="flex items-baseline justify-between">
                <p className={`text-xs ${active === t.key ? "text-white/65" : "text-foreground/55"}`}>{t.focus}</p>
                <span className="font-display font-black text-sm">{t.price}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="app-card-dark p-8 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#A8D5BA]/15 blur-2xl" />
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3 relative">
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-[-0.03em]">{current.name}</h2>
            <span className="tag-pill tag-pill-dark font-display font-black text-sm">{current.price}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-6 relative">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-65 mb-1">Foco</div>
              <p className="text-sm font-medium">{current.focus}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-65 mb-1">Responsabilidad real</div>
              <p className="text-sm font-medium">{current.responsibility}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-65 mb-1">Qué vendes en realidad</div>
              <p className="text-sm font-medium italic">{current.sells}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-65 mb-1">Evita</div>
              <p className="text-sm opacity-75">{current.avoid}</p>
            </div>
          </div>
          <div className="mt-8 relative">
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-65 mb-3">Decisiones típicas de este nivel</div>
            <div className="flex flex-wrap gap-2">
              {current.typical.map((x) => (
                <span key={x} className="text-xs font-medium px-3 py-1.5 bg-white/10 rounded-full border border-white/15">{x}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-display text-3xl md:text-4xl font-black tracking-[-0.03em] mb-5">Detectores estratégicos</h2>
        <Tabs defaultValue="scope" className="w-full">
          <TabsList className="bg-white border border-black/[0.08] rounded-2xl p-1 grid grid-cols-2 h-auto" data-testid="tabs-detectors">
            <TabsTrigger value="scope" data-testid="tab-scope" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">Inflación de scope</TabsTrigger>
            <TabsTrigger value="price" data-testid="tab-price" className="rounded-xl data-[state=active]:bg-foreground data-[state=active]:text-white py-2.5 text-xs font-semibold">Alerta de infravaloración</TabsTrigger>
          </TabsList>

          <TabsContent value="scope" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-5">
              <div className="app-card p-6 space-y-4">
                <div>
                  <Label className={lbl}>Entregables actuales</Label>
                  <Textarea data-testid="input-scope-deliverables" rows={6} value={scopeForm.deliverables} onChange={(e) => setScopeForm({ ...scopeForm, deliverables: e.target.value })} className={taBase} />
                </div>
                <div>
                  <Label className={lbl}>Resultados estratégicos declarados</Label>
                  <Textarea rows={3} value={scopeForm.strategic_outcomes} onChange={(e) => setScopeForm({ ...scopeForm, strategic_outcomes: e.target.value })} className={taBase} />
                </div>
                <button onClick={runScope} disabled={scopeLoading} data-testid="run-scope-btn" className="btn-apple text-sm">
                  {scopeLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analizando</> : <><Sparkles className="w-4 h-4" /> Detectar inflación</>}
                </button>
              </div>
              <div className="app-card p-6 min-h-[300px]">
                {!scopeResult ? (
                  <div className="h-full grid place-items-center text-center">
                    <div>
                      <div className="w-10 h-10 rounded-full bg-black/[0.04] grid place-items-center mx-auto mb-3"><AlertTriangle className="w-4 h-4 text-foreground/40" /></div>
                      <p className="text-sm text-foreground/55">El veredicto del detector aparecerá aquí.</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 flex-wrap">
                      <AlertTriangle className="w-5 h-5 text-[#ED773C]" strokeWidth={2.5} />
                      <div className="font-display text-2xl font-black tracking-[-0.02em]">{scopeResult.verdict}</div>
                      <span className="font-display font-black text-foreground/45">{scopeResult.inflation_score}/100</span>
                    </div>
                    <p className="text-sm leading-relaxed">{scopeResult.summary}</p>
                    {!!scopeResult.redundant_items?.length && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-foreground/55 mb-2">Entregables redundantes</div>
                        <ul className="text-sm text-foreground/70 space-y-1">{scopeResult.redundant_items.map((x, i) => <li key={i}>— {x}</li>)}</ul>
                      </div>
                    )}
                    {!!scopeResult.strategic_replacements?.length && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-foreground mb-2">Reemplazos estratégicos</div>
                        <ul className="text-sm space-y-1">{scopeResult.strategic_replacements.map((x, i) => <li key={i}>— <span className="line-through text-foreground/55">{x.replace}</span> → <span className="font-semibold">{x.with}</span></li>)}</ul>
                      </div>
                    )}
                    <div className="bg-[#FCF1CD] rounded-2xl p-4">
                      <div className="text-[10px] uppercase tracking-wider font-bold mb-1">Consejo</div>
                      <p className="text-sm leading-relaxed">{scopeResult.advice}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="price" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-5">
              <div className="app-card p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className={lbl}>Precio actual</Label>
                    <Input data-testid="input-price-current" type="number" value={priceForm.current_price} onChange={(e) => setPriceForm({ ...priceForm, current_price: e.target.value })} className={fieldBase} />
                  </div>
                  <div>
                    <Label className={lbl}>Moneda</Label>
                    <Input value={priceForm.currency} onChange={(e) => setPriceForm({ ...priceForm, currency: e.target.value })} className={fieldBase} />
                  </div>
                </div>
                <div>
                  <Label className={lbl}>Tipo de servicio</Label>
                  <Input value={priceForm.service_type} onChange={(e) => setPriceForm({ ...priceForm, service_type: e.target.value })} className={fieldBase} />
                </div>
                <div>
                  <Label className={lbl}>Tamaño del cliente</Label>
                  <Select value={priceForm.company_size} onValueChange={(v) => setPriceForm({ ...priceForm, company_size: v })}>
                    <SelectTrigger className={fieldBase}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="freelancer">Freelancer</SelectItem>
                      <SelectItem value="small">Pequeña empresa</SelectItem>
                      <SelectItem value="startup">Startup</SelectItem>
                      <SelectItem value="mid">Mediana empresa</SelectItem>
                      <SelectItem value="multinational">Multinacional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={lbl}>Entregables</Label>
                  <Textarea rows={4} value={priceForm.deliverables} onChange={(e) => setPriceForm({ ...priceForm, deliverables: e.target.value })} placeholder="Uno por línea" className={taBase} />
                </div>
                <div>
                  <Label className={lbl}>Responsabilidad estratégica</Label>
                  <Textarea rows={2} value={priceForm.strategic_responsibility} onChange={(e) => setPriceForm({ ...priceForm, strategic_responsibility: e.target.value })} className={taBase} />
                </div>
                <button onClick={runPrice} disabled={priceLoading} data-testid="run-price-btn" className="btn-apple text-sm">
                  {priceLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Chequeando</> : <><TrendingUp className="w-4 h-4" /> Chequear pricing</>}
                </button>
              </div>
              <div className="app-card p-6 min-h-[300px]">
                {!priceResult ? (
                  <div className="h-full grid place-items-center text-center">
                    <div>
                      <div className="w-10 h-10 rounded-full bg-black/[0.04] grid place-items-center mx-auto mb-3"><TrendingUp className="w-4 h-4 text-foreground/40" /></div>
                      <p className="text-sm text-foreground/55">El veredicto del pricing aparecerá aquí.</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 flex-wrap">
                      <TrendingUp className="w-5 h-5 text-emerald-600" strokeWidth={2.5} />
                      <div className="font-display text-2xl font-black tracking-[-0.02em]">{priceResult.verdict}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[#E8F4ED] rounded-2xl p-4 border border-[#BBE3CB]">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-[#1B6B3F]">Rango justo</div>
                        <div className="font-display text-xl font-black mt-1 tracking-[-0.02em]">{priceResult.fair_range}</div>
                      </div>
                      <div className="bg-[#FCF1CD] rounded-2xl p-4 border border-[#F0DC95]">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-[#8A6914]">Anchor premium</div>
                        <div className="font-display text-xl font-black mt-1 tracking-[-0.02em]">{priceResult.premium_anchor_price}</div>
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-foreground mb-2">Razones</div>
                      <ul className="text-sm space-y-1.5">{priceResult.reasoning?.map((r, i) => <li key={i} className="text-foreground/75">— {r}</li>)}</ul>
                    </div>
                    <div className="bg-black/[0.03] rounded-2xl p-4 border border-black/[0.06]">
                      <div className="text-[10px] uppercase tracking-wider font-bold mb-1">Cambio de posicionamiento</div>
                      <p className="text-sm leading-relaxed">{priceResult.positioning_shift}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
